import Button from '@pelckmans/elementis/components/button';
import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import InfoIcon from '@pelckmans/elementis/icons/info';
import PelckmansIcon from '@pelckmans/elementis/icons/pelckmans';
import SmartschoolIcon from '@pelckmans/elementis/icons/smartschool';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../contexts/ModalContext';
import ConfirmationModal from '../../../shared-components/ConfirmationModal';
import formatTeacherName from '../../../utils/formatTeacherName';
import { disconnectGroup, StudentGroup } from '../api/my-teachers-api';
import useStudentGroups from '../hooks/useStudentGroups';

export default function AcceptedList({
  groups,
  headerTitle,
  headerTooltip,
  allowDisconnect = false,
  schoolYear,
}: {
  groups: StudentGroup[];
  headerTitle: string;
  headerTooltip: string;
  allowDisconnect?: boolean;
  schoolYear: string;
}) {
  const { t } = useTranslation();

  const { refetch } = useStudentGroups(schoolYear);
  const { showModal } = useContext(ModalContext);

  const disconnect = async (groupId: string) => {
    await disconnectGroup(groupId);
    refetch(schoolYear);
  };

  const handleDisconnect = async (group: StudentGroup) => {
    showModal(
      <ConfirmationModal
        id="disconnect-confirmation-modal"
        confirmationText={t('PAGES.MY_TEACHERS.ACCEPTED_LIST.MODALS.DISCONNECT.ACTIONS.CONFIRM')}
        headerTitle={t('PAGES.MY_TEACHERS.ACCEPTED_LIST.MODALS.DISCONNECT.TITLE', {
          teacher: formatTeacherName(group.owner),
        })}
        message={t('PAGES.MY_TEACHERS.ACCEPTED_LIST.MODALS.DISCONNECT.MESSAGE')}
        onConfirm={() => disconnect(group.id)}
      />,
    );
  };

  return (
    <List emptyMessage={t('PAGES.MY_TEACHERS.ACCEPTED_LIST.EMPTY')}>
      <List.Header
        title={headerTitle}
        titleTag="h3"
        prominent
        tooltip={
          <Tooltip anchor={<InfoIcon data-testid="info-icon" />}>
            <p>{headerTooltip}</p>
          </Tooltip>
        }
      />
      {groups.map(group => (
        <List.Item
          key={group.id}
          title={formatTeacherName(group.owner)}
          subText={group.name}
          data-testid={`teacher-${group.id}`}
          pillGroupProps={{
            role: 'group',
            'aria-label': t('PAGES.MY_TEACHERS.ACCEPTED_LIST.PILL_GROUP_PROPS.A11Y_LABEL'),
            children: group.subjects.map((s, i) => <Pill key={`${i}-${s}`}>{s}</Pill>),
          }}
          actionArea={
            allowDisconnect ? (
              <Button prominence="neutral" size="sm" onClick={() => handleDisconnect(group)}>
                {t('PAGES.MY_TEACHERS.ACCEPTED_LIST.ACTIONS.DISCONNECT')}
              </Button>
            ) : undefined
          }
          icon={group.smartschoolGroupId ? <SmartschoolIcon /> : <PelckmansIcon />}
        />
      ))}
    </List>
  );
}
